import { Box, useTheme } from '@mui/material';
import React, { EventHandler, MouseEventHandler, MutableRefObject, SyntheticEvent } from 'react';
import ReactDropzone, { FileRejection } from 'react-dropzone';

import useSnackbar from '../../../hooks/useSnackbar';
import { Margin } from '../../../theme/types';
import { HandleFileChange } from '../hooks/useFileInput';

import DropzoneClear from './DropzoneClear';
import DropzoneInner from './DropzoneInner';

export interface DropzoneProps {
  clearable?: boolean;
  encodingFormat?: string;
  fileName?: string;
  encodingFormatTypes?: string;
  inputRef: MutableRefObject<HTMLInputElement | null>;
  maxSize?: number;
  name: string;
  onChange: HandleFileChange;
  openDialog: EventHandler<SyntheticEvent<unknown>>;
  preview?: string;
}

const Dropzone: React.FC<DropzoneProps> = ({
  clearable,
  encodingFormat,
  encodingFormatTypes,
  fileName,
  inputRef,
  maxSize,
  onChange,
  openDialog,
  preview,
}) => {
  const showSnackbar = useSnackbar();
  const theme = useTheme();
  const onClear = React.useCallback<MouseEventHandler>((e) => {
    e.preventDefault();

    onChange(undefined);
  }, []);

  const onDrop = React.useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    const error = rejectedFiles?.[0]?.errors?.[0];

    if (error) {
      showSnackbar(error.message);
    }

    const [file] = acceptedFiles;

    if (file) {
      onChange(file);
    }
  }, []);

  return (
    <ReactDropzone accept={encodingFormatTypes} maxSize={maxSize} multiple={false} onDrop={onDrop}>
      {({ getInputProps, getRootProps, isDragActive }) => (
        <Box
          sx={{
            '& img': {
              maxWidth: '100%',
              minWidth: '100px',
            },
            boxSizing: 'border-box',
            display: 'block',
            marginBottom: '1rem',
            position: 'relative',
            width: '100%',
          }}
        >
          <DropzoneInner
            encodingFormat={encodingFormat}
            fileName={fileName}
            isDragActive={isDragActive}
            preview={preview}
          >
            {(renderedPreview: React.ReactNode) => (
              <React.Fragment>
                {!clearable && preview && <DropzoneClear onClear={onClear} />}
                <Box
                  component="button"
                  type="button"
                  {...getRootProps({ onClick: openDialog })}
                  sx={{
                    '&:hover': {
                      borderStyle: 'dotted',
                    },
                    border: `2px dashed ${theme.palette.grey.main}`,
                    borderStyle: isDragActive ? 'solid' : undefined,
                    color: theme.palette.grey.main,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: theme.spacing(Margin.Medium),
                    width: '100%',
                  }}
                >
                  {renderedPreview}
                  <Box
                    component="input"
                    {...getInputProps()}
                    ref={inputRef}
                    sx={{
                      display: 'none',
                    }}
                    type="file"
                  />
                </Box>
              </React.Fragment>
            )}
          </DropzoneInner>
        </Box>
      )}
    </ReactDropzone>
  );
};

export default Dropzone;
